import { supabase } from "@/integrations/supabase/client";
import { type Unit } from "@/types";

export const unitsAPI = {
  // Get all units for a specific project
  async getProjectUnits(projectId: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('units')
      .select('*')
      .eq('project_id', projectId)
      .order('type')
      .order('number');

    if (error) {
      return { data: null, error };
    }

    // Transform data from DB format to frontend format
    const transformedData = data.map(unit => ({
      type: unit.type as 'flat' | 'shop',
      number: unit.number,
      area: unit.area || 0
    }));

    return { data: transformedData as Unit[], error: null };
  },

  // Get a single unit by project ID, type, and number
  async getUnit(projectId: string, type: string, number: number) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    const { data, error } = await supabase
      .from('units')
      .select('*')
      .eq('project_id', projectId)
      .eq('type', type)
      .eq('number', number)
      .single();

    if (error) {
      return { data: null, error };
    }

    // Transform unit data to frontend format
    const transformedUnit: Unit = {
      type: data.type as 'flat' | 'shop',
      number: data.number,
      area: data.area || 0
    };

    return { data: transformedUnit, error: null };
  },

  // Create a new unit
  async createUnit(projectId: string, unitData: Unit) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Insert the unit
    const { data, error } = await supabase
      .from('units')
      .insert({
        project_id: projectId,
        type: unitData.type,
        number: unitData.number,
        area: unitData.area
      })
      .select()
      .single();

    if (error) {
      return { data: null, error };
    }

    return { data: data.id, error: null };
  },

  // Update an existing unit
  async updateUnit(id: string, unitData: Partial<Unit>) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Update the unit
    const { data, error } = await supabase
      .from('units')
      .update({
        type: unitData.type,
        number: unitData.number,
        area: unitData.area,
        updated_at: new Date().toISOString()
      })
      .eq('id', id)
      .select()
      .single();

    if (error) {
      return { data: null, error };
    }

    return { data: data, error: null };
  },

  // Delete a unit
  async deleteUnit(id: string) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // Delete the unit
    const { data, error } = await supabase
      .from('units')
      .delete()
      .eq('id', id);

    return { data, error };
  },

  // Batch create or update units for a project
  async batchUpsertUnits(projectId: string, units: Unit[]) {
    const { data: { session } } = await supabase.auth.getSession();
    
    if (!session) {
      return { data: null, error: new Error('User not authenticated') };
    }

    // First delete existing units for the project
    await supabase
      .from('units')
      .delete()
      .eq('project_id', projectId);

    if (units.length === 0) {
      return { data: true, error: null };
    }

    // Transform units to DB format
    const unitsToInsert = units.map(unit => ({
      project_id: projectId,
      type: unit.type,
      number: unit.number,
      area: unit.area
    }));

    // Insert new units
    const { data, error } = await supabase
      .from('units')
      .insert(unitsToInsert)
      .select();

    if (error) {
      return { data: null, error };
    }

    return { data: true, error: null };
  }
}; 